// World factory — encapsulates planet + props + sky + cycle so the same
// function can build N planets in a future multi-planet lobby. Async because
// prop placement may load GLTF assets in a later task.

import * as THREE from 'three';
import { buildPlanet } from './planet.js';
import { makeCycle } from './cycle.js';
import { populatePlanet } from './props.js';
import { makeStarfield, makeBlackHole, makeAurora, makeComets } from './sky.js';
import { makeToonMaterial } from '../render/flat.js';

const DEFAULTS = {
  radius: 30,
  detail: 4,
  jitter: 0.45,
  seed: 13,
  palette: {
    rock: 0x4d4072,
    mossTop: 0x6a4f8a,
    mossDark: 0x2c2046,
    auroraPalette: [0xb84dff, 0xff5dc7, 0x7a4dff],
  },
  cycleSeconds: 60,
};

export async function buildWorld(scene, userConfig = {}) {
  const config = {
    ...DEFAULTS,
    ...userConfig,
    palette: { ...DEFAULTS.palette, ...(userConfig.palette || {}) },
  };

  const planet = buildPlanet({
    radius: config.radius, detail: config.detail, jitter: config.jitter, seed: config.seed,
  });

  const planetGroup = new THREE.Group();
  scene.add(planetGroup);

  // Rock body
  const rockMat = makeToonMaterial({ color: config.palette.rock });
  const rock = new THREE.Mesh(planet.geometry, rockMat);
  planetGroup.add(rock);

  // Vertex-colored moss cap (tightly coupled to the planet's geometry)
  const capGeo = planet.geometry.clone();
  const colors = new Float32Array(capGeo.attributes.position.count * 3);
  const v = new THREE.Vector3();
  const top = new THREE.Color(config.palette.mossTop);
  const dark = new THREE.Color(config.palette.mossDark);
  for (let i = 0; i < capGeo.attributes.position.count; i++) {
    v.fromBufferAttribute(capGeo.attributes.position, i);
    const c = dark.clone().lerp(top, Math.max(0, v.clone().normalize().y));
    colors[i*3] = c.r; colors[i*3+1] = c.g; colors[i*3+2] = c.b;
    v.multiplyScalar(1.002);
    capGeo.attributes.position.setXYZ(i, v.x, v.y, v.z);
  }
  capGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  capGeo.computeVertexNormals();
  const capMat = new THREE.MeshStandardMaterial({
    vertexColors: true, flatShading: true, roughness: 1.0, metalness: 0.0,
  });
  const cap = new THREE.Mesh(capGeo, capMat);
  planetGroup.add(cap);

  // Sky kit
  const stars = makeStarfield({ count: 2200 });
  scene.add(stars);
  const blackHole = makeBlackHole();
  scene.add(blackHole);
  const aurora = makeAurora({ palette: config.palette.auroraPalette });
  scene.add(aurora);
  const comets = makeComets({ count: 5 });
  scene.add(comets);

  // Cycle is created here but needs lights wired by startGame() — the lights
  // are scene-level state owned by startGame, not world-config. So the caller
  // is responsible for passing them in via userConfig.cycleLights = { keyLight, ambient }.
  const cycleLights = userConfig.cycleLights || {};
  const cycle = makeCycle(scene, {
    cycleSeconds: config.cycleSeconds,
    keyLight: cycleLights.keyLight,
    ambient: cycleLights.ambient,
  });

  // Props (async — future GLTF loading lives in populatePlanet)
  const { props, huts } = await populatePlanet(scene, planet);

  return { planet, planetGroup, rock, cap, props, huts, stars, blackHole, aurora, comets, cycle };
}
